import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { deleteCookie } from "utils/storage";
import { logoutAccount } from "services/loginPage";

import Button from "components/Button";
import LiveHelpIcon from "@mui/icons-material/LiveHelp";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import useScreenMobile from "hooks/useScreenMobile";
import getDomainName from "custom/getDomainName";

import { changeLanguage, setOTPVerifiedStatus } from "store/actions";

import companyLogo from "assets/images/header/pixFirstLogo.svg";
import languageLogo from "assets/images/header/usEnglishLogo.png";
import frenchLogo from "assets/images/header/usFrenchLogo.svg";
import languageDropdown from "assets/images/header/languageDropdown.svg";
import profileLogo from "assets/images/header/profileLogo.svg";
import cartLogo from "assets/images/header/buy.svg";
import divider from "assets/images/header/divider.svg";
import bagLogo from "assets/images/header/Bag.svg";
import profileIcon from "assets/images/header/profileIcon.svg";
import logOutIcon from "assets/images/header/Logout.svg";
import resetPwd from "assets/images/myProfile/Unlock.svg";
// import profileMobileIcon from "assets/images/accessPhoto/Profile.svg";

import "components/Header/header.scss";
import calculateTotals from "custom/calculateTotalCart";
import { decodeToken } from "utils/tokens";

const Header = ({ isVisible = true }) => {
  const { t, i18n } = useTranslation();
  const [isDropdown, setIsDropdown] = useState(false);
  const [isLoginDropdown, setIsLoginDropdown] = useState(false);
  const [position, setPosition] = useState(0);
  const [hideHeader, setHideHeader] = useState(true);
  const [cartData, setCartData] = useState(null);
  const cart = useSelector((state) => state.cartData);
  const loading = useSelector((state) => state.loading);
  // const profileData = useSelector((store) => store.profileData);
  const cartLoading = useSelector((state) => state.cartLoading);
  const language = useSelector((state) => state.language);
  const accessCardData = useSelector((state) => state.accessCardData);

  const navigate = useNavigate();
  const location = useLocation();

  const isMobile = useScreenMobile({ size: 992 });
  const dispatch = useDispatch();
  
  // Check if we're in school mode
  const isSchoolMode = sessionStorage.getItem("is_school") === "true";
  const schoolOrgId = sessionStorage.getItem("school_org_id");

  const languages = [
    { name: "English", index: 0, value: "en", image: languageLogo },
    { name: "Français", index: 1, value: "fr", image: frenchLogo }
  ];

  const [activeIndex, setActiveIndex] = useState(
    languages.findIndex((lang) => lang.value === language)
  );

  const accessToken = localStorage.getItem("access_token");
  const refreshToken = localStorage.getItem("refresh_token");
  const tokenToUse = accessToken || refreshToken;

  let decodedUser = null;
  let displayName = "";

  try {
    if (tokenToUse) {
      decodedUser = decodeToken(tokenToUse);
      displayName = decodedUser?.name || decodedUser?.email || "";
    }
  } catch (err) {
    console.warn("Invalid token format", err);
    displayName = "";
  }

  useEffect(() => {
    const currentIndex = languages.findIndex((lang) => lang.value === language);
    if (currentIndex !== -1) {
      setActiveIndex(currentIndex);
    }
    i18n.changeLanguage(language);
  }, [language, i18n]);

  useEffect(() => {
    if (!cartLoading) {
      setCartData(cart);
    }
  }, [cart, loading, cartLoading]);

  useEffect(() => {
    const handleScroll = () => {
      const moving = window.pageYOffset;
      if (moving > 100) {
        setHideHeader(position > moving);
      } else {
        setHideHeader(true);
      }
      setPosition(moving);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  });
  const cls = hideHeader ? "" : "hidden";

  const handleLanguageChange = (index, val) => {
    sessionStorage.setItem("pFlanguage", val);
    dispatch(changeLanguage(val));
    setActiveIndex(index);
    setIsDropdown(false);
  };

  const logoClassName =
    window.location.pathname === "/parents" ||
      window.location.pathname === "/parents/home"
      ? "headerCompanyLogo"
      : "headerCompanyLogo";

  const headerLogo =
    window.location.pathname !== "/parents/login" ? "loginScreen" : "";


  const handleNavigate = () => {
    navigate("/parents/profile-screen", {
      state: { activeProfileIndex: "myOrders" }
    });
    setIsLoginDropdown(false);
  };

  const moveToProfileScreen = () => {
    navigate("/parents/profile-screen/", {
      state: { activeProfileIndex: "myProfile" }
    });
    setIsLoginDropdown(false);
  };

  const goToCart = () => {
    navigate("/parents/view-cart");
  };

  const handleBackToSchool = () => {
    let orgId = schoolOrgId;
    
    if (!orgId && accessCardData && accessCardData.access_ids) {
      const firstAccessId = Object.keys(accessCardData.access_ids)[0];
      if (firstAccessId && accessCardData.access_ids[firstAccessId].org) {
        orgId = accessCardData.access_ids[firstAccessId].org.id;
      }
    }
    
    if (orgId) {
      window.location.href = `/school/${orgId}/access-cards/`;
    }
  };

  const handleLoginClick = () => {
    const currentPath = location.pathname + location.search;
    navigate(`/parents/login?redirect=${encodeURIComponent(currentPath)}`);
  };

  const handleLoginClickOrders = () => {
    navigate(
      `/parents/login?redirect=${encodeURIComponent("/parents/profile-screen")}`,
      {
        state: { activeProfileIndex: "myOrders" }
      }
    );
  };

  const cartTotals =
    cartData && Object.keys(cartData).length > 0
      ? calculateTotals(cartData)
      : null;
  const dropdownRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsLoginDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  function handleClickInside(e) {
    e.stopPropagation();
  }

  const domainName = getDomainName();

  return (
    <header className={`header ${cls}`} style={{
      transform: isVisible ? "translateY(0)" : "translateY(-100%)",
      transition: "transform 0.3s ease"
    }}>
      <div className="container headerDesign">
        {/* header */}
        <div className={`${logoClassName} ${headerLogo}`}>
          {!isMobile &&
            (["Pixfirst", "Shopune"].includes(domainName) ? (
              isSchoolMode ? (
                <img
                  className="companyLogo"
                  src={companyLogo}
                  alt="companyLogo"
                  style={{ cursor: "default" }}
                />
              ) : (
                <Link to={"/parents/home"}>
                  <img
                    className="companyLogo"
                    src={companyLogo}
                    alt="companyLogo"
                  />
                </Link>
              )
            ) : (
              <div className="companyNameAsLogo">
                {isSchoolMode ? (
                  <span style={{ cursor: "default" }}>{domainName}</span>
                ) : (
                  <Link to={"/parents/home"}>{domainName}</Link>
                )}
              </div>
            ))}

          {/* Back to School Button */}
          {isSchoolMode && !isMobile && (
            <div
              onClick={handleBackToSchool}
              style={{
                display: "flex",
                alignItems: "center",
                marginLeft: "20px",
                cursor: "pointer",
                padding: "8px 16px",
                backgroundColor: "#1976d2",
                color: "#fff",
                borderRadius: "4px",
                fontSize: "14px",
                fontWeight: "500",
                transition: "background-color 0.3s"
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#1565c0"}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#1976d2"}
            >
              <ArrowBackIcon style={{ marginRight: "6px", fontSize: "18px" }} />
              <span>{t("header.backToSchool")}</span>
            </div>
          )}

          {!isMobile && (
            <>
              <div
                className="headerLanguage"
                onClick={() => setIsDropdown(!isDropdown)}
              >
                <img
                  className="svgLogo"
                  src={languages[activeIndex].image}
                  alt="languageLogo"
                  style={{ marginRight: isMobile ? "5px" : "" }}
                />
                {!isMobile && (
                  <span className="headerLanguageName">
                    {languages[activeIndex].name}
                  </span>
                )}
                <img
                  className={`arrowDropdown ${isDropdown ? "headerLoginDownImg" : "headerLoginUpImg"}`}
                  src={languageDropdown}
                  alt="toggleDropdown"
                />
              </div>

              {isDropdown && (
                <div className="headerLanguageDropdown">
                  {languages.map((language, index) => (
                    <div
                      key={index}
                      className={`headerLanguageLogo ${activeIndex === index ? "languageActive" : ""}`}
                      onClick={() =>
                        handleLanguageChange(index, language.value)
                      }
                    >
                      <img src={language.image} alt="languageLogo" />
                      <span className="headerLanguageName">
                        {language.name}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Mobile Language Dropdown - ONLY for login page */}
          {isMobile && location.pathname === "/parents/login" && (
            <div style={{ position: "relative" }}>
              <div
                className="headerLanguage"
                onClick={() => setIsDropdown(!isDropdown)}
              >
                <img
                  className="svgLogo"
                  src={languages[activeIndex].image}
                  alt="languageLogo"
                  style={{ marginRight: "5px" }}
                />
                <span className="headerLanguageName">
                  {languages[activeIndex].name}
                </span>
                <img
                  className={`arrowDropdown ${isDropdown ? "headerLoginDownImg" : "headerLoginUpImg"}`}
                  src={languageDropdown}
                  alt="toggleDropdown"
                />
              </div>

              {isDropdown && (
                <div className="headerLanguageDropdown">
                  {languages.map((language, index) => (
                    <div
                      key={index}
                      className={`headerLanguageLogo ${activeIndex === index ? "languageActive" : ""}`}
                      onClick={() =>
                        handleLanguageChange(index, language.value)
                      }
                    >
                      <img src={language.image} alt="languageLogo" />
                      <span className="headerLanguageName">
                        {language.name}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* login */}
        {window.location.pathname !== "/parents/login" && (
            <div
              className="headerLogin"
              ref={dropdownRef}
              onClick={handleClickInside}
            >
              {/* Back to School Button for Mobile */}
              {isSchoolMode && isMobile && (
                <>
                  <div
                    className="headerLoginButton"
                    onClick={handleBackToSchool}
                    style={{ height: 24, width: 24 }}
                  >
                    <ArrowBackIcon
                      style={{ color: "#1976d2", height: "100%", width: "100%" }}
                    />
                  </div>
                  <div className="headerLoginSeparator">
                    <img src={divider} alt="dividerLogo" />
                  </div>
                </>
              )}
              {/* Hide FAQ in school mode */}
              {!isSchoolMode && (
                <div
                  className="headerLoginButton"
                  onClick={() => window.open("/parents/faq", "_blank")}
                  style={{ height: 21, width: 21 }}
                >
                  <LiveHelpIcon
                    style={{ color: "#1976d2", height: "100%", width: "100%" }}
                  />
                </div>
              )}
              {isMobile && (
                <div className="headerLoginSeparator">
                  <img src={divider} alt="dividerLogo" />
                </div>
              )}
              {isMobile && (
                <>
                  <div
                    className="headerLanguage"
                    onClick={() => setIsDropdown(!isDropdown)}
                  >
                    <img
                      className="svgLogo"
                      src={languages[activeIndex].image}
                      alt="languageLogo"
                      style={{ marginRight: isMobile ? "5px" : "" }}
                    />
                    {!isMobile && (
                      <span className="headerLanguageName">
                        {languages[activeIndex].name}
                      </span>
                    )}
                    <img
                      className={`arrowDropdown ${isDropdown ? "headerLoginDownImg" : "headerLoginUpImg"}`}
                      src={languageDropdown}
                      alt="toggleDropdown"
                    />
                  </div>

                  {isDropdown && (
                    <div
                      className="headerLanguageDropdown"
                      style={{ right: "30%" }}
                    >
                      {languages.map((language, index) => (
                        <div
                          key={index}
                          className={`headerLanguageLogo ${activeIndex === index ? "languageActive" : ""}`}
                          onClick={() =>
                            handleLanguageChange(index, language.value)
                          }
                        >
                          <img src={language.image} alt="languageLogo" />
                          <span className="headerLanguageName">
                            {language.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              <div className="headerLoginSeparator">
                <img src={divider} alt="dividerLogo" />
              </div>
              <div
                className="headerLoginButton"
                onClick={() => {
                  setIsLoginDropdown(!isLoginDropdown);
                }}
              >
                {isMobile ? (
                  <>
                    <span
                      style={{
                        top: 0,
                        right: 0,
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        backgroundColor: localStorage?.getItem("access_token")
                          ? "lightseagreen"
                          : "red",
                        marginRight: 5
                      }}
                      title="Logged In"
                    />
                    <img
                      className="svgLogos"
                      src={profileLogo}
                      alt="profileLogo"
                    />
                    <img
                      src={languageDropdown}
                      className={
                        isLoginDropdown
                          ? "headerLoginDownImg"
                          : "headerLoginUpImg"
                      }
                      alt="languageDropdown"
                    />
                  </>
                ) : (
                  <>
                    <span
                      style={{
                        top: 0,
                        right: 0,
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        backgroundColor: localStorage?.getItem("access_token")
                          ? "lightseagreen"
                          : "red",
                        marginRight: 5
                      }}
                      title="Logged In"
                    />
                    <img
                      className="svgLogo"
                      src={profileLogo}
                      alt="profileLogo"
                    />
                    <label className="headerLanguageName">
                      {localStorage?.getItem("access_token")
                        ? displayName
                        : t("header.title")}
                    </label>
                    <img
                      src={languageDropdown}
                      className={
                        isLoginDropdown
                          ? "headerLoginDownImg"
                          : "headerLoginUpImg"
                      }
                      alt="languageDropdown"
                    />
                  </>
                )}
              </div>
              <div className="headerLoginSeparator">
                <img src={divider} alt="dividerLogo" />
              </div>

              {localStorage.getItem("access_token") ? (
                ""
              ) : (
                <>
                  {isLoginDropdown && (
                    <div className="headerLoginDropdown headerLanguageDropdown">
                      {/* <h3>{t("header.helloUser")}</h3>
                      <p>{t("header.accessAccount")}</p> */}
                      <Button className="newButton" onClick={handleLoginClick}>
                        {t("header.login")}
                      </Button>
                      <div className="separator"></div>

                      {/* <div
                        className="myOrder"
                        onClick={() => window.open("/parents/faq", "_blank")}
                      >
                        <LiveHelpIcon
                          style={{ color: "#1976d2", marginRight: "8px" }}
                        />
                        <text>{t("common.help")}</text>
                      </div> */}
                      <div className="myOrder" onClick={handleLoginClickOrders}>
                        <img src={bagLogo} alt="bagLogo" />
                        <text>{t("header.myOrders")}</text>
                      </div>
                    </div>
                  )}
                </>
              )}

              {localStorage?.getItem("access_token") ? (
                <>
                  {isLoginDropdown && (
                    <div className="headerLoginDropdown headerLanguageDropdown">
                      <div className="loginDetails">
                        <img src={profileIcon} alt="profileImage" />
                        <div className="loginDetailsName">
                          <h4> {displayName} </h4>
                          <span onClick={moveToProfileScreen}>
                            {t("header.viewProfile")}
                          </span>
                        </div>
                      </div>
                      <div className="separator"></div>

                      {/* <div
                        className="myOrder"
                        onClick={() => window.open("/parents/faq", "_blank")}
                      >
                        <LiveHelpIcon
                          style={{ color: "#1976d2", marginRight: "8px" }}
                        />
                        <text>{t("common.help")}</text>
                      </div> */}

                      <div className="myOrder" onClick={handleNavigate}>
                        <img src={bagLogo} alt="bagLogo" />
                        <text>{t("header.myOrder")}</text>
                      </div>

                      {/* Hide Reset Password and Logout in school mode */}
                      {!isSchoolMode && (
                        <>
                          <div
                            className="myOrder"
                            onClick={() => {
                              navigate("/parents/profile-screen", {
                                state: { activeProfileIndex: "resetPassword" }
                              });
                            }}
                          >
                            <img src={resetPwd} alt="resetPwd" />
                            <text>
                              {" "}
                              {t("profile.profileDetails.Reset Password")}{" "}
                            </text>
                          </div>

                          <div
                            className="myOrder"
                            onClick={async () => {
                              try {
                                // Call the logout API
                                await logoutAccount();
                              } catch (error) {
                                console.error("Logout API call failed", error);
                              }
                              localStorage.removeItem("access_token");
                              localStorage.removeItem("refresh_token");
                              localStorage.removeItem("activeProfileIndex");
                              localStorage.removeItem("storeValues");
                              localStorage.clear();
                              deleteCookie("accessJWT");
                              deleteCookie("refreshJWT");
                              dispatch(setOTPVerifiedStatus(null));

                              navigate("/parents/login");
                              setIsLoginDropdown(false);
                            }}
                          >
                            <img src={logOutIcon} alt="bagLogo" />
                            <text>{t("header.logout")}</text>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </>
              ) : (
                ""
              )}

              <div className="headerLoginCart" onClick={goToCart}>
                {isMobile ? (
                  <>
                    <img className="svgLogos" src={cartLogo} alt="cartLogo" />
                    <span className="headerCartCount">
                      {cartTotals != null ? cartTotals.totalProducts : "0"}
                    </span>
                  </>
                ) : (
                  <>
                    {/* <img className="svgLogo" src={cartLogo} alt="cartLogo" />
                    <span className="headerLanguageName">
                      {t("header.cart")}
                    </span>
                    <span className="headerCartCount">{cartTotals != null ? cartTotals.totalProducts : "0"}</span> */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        position: "relative"
                      }}
                    >
                      <img className="svgLogo" src={cartLogo} alt="cartLogo" />
                      <span className="headerCartCount">
                        {cartTotals != null ? cartTotals.totalProducts : "0"}
                      </span>
                    </div>
                    <span className="headerLanguageName">
                      {t("header.cart")}
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

        {/* {window.location.pathname === "/parents" && (
          <>
            {isMobile ? (
              <img
                className="headerLoginIcon"
                src={profileMobileIcon}
                alt="image"
                onClick={() => navigate("/parents/login")}
              />
            ) : (
              <div className="headerLogin">
                <p className="headerLoginPara">{t("header.accessAccount")}</p>
                <Button
                  onClick={() => navigate("/parents/login")}
                  className="headerLoginBtn"
                >
                  {t("header.title")}
                </Button>
              </div>
            )}
          </>
        )} */}
      </div>
    </header>
  );
};

export default Header;
